import { FC, useState, MouseEvent } from "react";
import cx from "classnames";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { ClickableCard } from "@/components/Card";
import Typography from "@/components/Typography";
import Button, { IconButton } from "@/components/Button";
import {
  SaveIcon,
  LocationIcon,
  BuildingIcon,
  GlobalDollarIcon,
  IndustryIcon,
} from "@/components/Icon";
import { usePersonalDetailsQuery } from "@/fetch/profiles";
import {
  useResponsive,
  useDialog,
  useSnackbar,
  useTrackers,
  useOpenExternalURL,
} from "@/hooks";
import {
  useCreateSavedJobMutation,
  useDeleteSavedJobMutation,
} from "@/fetch/trip";
import { JobType, JOB_STATUS } from "@/fetch/suppliers";
import styles from "./ExternalJobCard.module.scss";

type Props = {
  job?: JobType;
  isOnSaveTab?: boolean;
  onSave: () => void;
};

const JobCard: FC<Props> = ({ job, isOnSaveTab, onSave }) => {
  const [showFallbackImage, setShowFallbackImage] = useState<boolean>(false);
  const { data: personalDetails, isLoading: isPersonalDetailsLoading } =
    usePersonalDetailsQuery();
  const { mutateAsync: createSaved, isLoading: isCreatingSaved } =
    useCreateSavedJobMutation();
  const { mutateAsync: removeSaved, isLoading: isRemovingSaved } =
    useDeleteSavedJobMutation();

  const { isMobile } = useResponsive();
  const { openDialog } = useDialog("job");
  const openExternalURL = useOpenExternalURL();
  const { replace, pathname, query } = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const { track } = useTrackers();

  const isSaved = job?.is_saved;

  const isActive = job?.status === JOB_STATUS.Active;
  const postedDate = Boolean(job?.start_date)
    ? ` ${dayjs(Number(job?.start_date) * 1000).format("MMMM")}`
    : "N/A";

  const skills = Boolean(job?.skill_set) ? job?.skill_set.split(",") || [] : [];

  const logoImageUrl = `https://gwatco-res.cloudinary.com/image/upload/v1684225405/${job?.s3_logo}`;

  const handleSave = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!isSaved) {
      await createSaved({ job_id: job?.id as number });
      enqueueSnackbar("Saved Successfully!", {
        variant: "success",
      });
      onSave();
    } else {
      await removeSaved({ savedJobId: job?.saved_id as number });
      enqueueSnackbar("Unsaved Successfully!", {
        variant: "success",
      });
      onSave();
    }
  };

  const addFallbackImage = () => {
    setShowFallbackImage(true);
  };

  const handleOpenDialog = () => {
    track("Job Card Clicked", {
      eventId: "job-card-clicked",
      jobId: job?.id,
    });
    openDialog({ id: job?.id });
  };

  const getDomain = () => {
    let currentUrl = job?.source_url || "";
    if (!currentUrl.startsWith("http")) {
      currentUrl = "http://" + currentUrl;
    }

    return new URL(currentUrl).hostname.replace("www.", "");
  };

  const goToJob = () => {
    openExternalURL(job?.source_url || "");
    track("Job Link Clicked", {
      eventId: "job-link-clicked",
      jobId: job?.id || "",
    });
  };

  return (
    <>
      <ClickableCard
        className={cx(styles.card, { [styles.disabledCard]: !isActive })}
        onClick={handleOpenDialog}
        outlined={!isActive}
      >
        <div className={styles.container}>
          <div
            className={cx(styles.leftContainer, {
              [styles.leftContainerSaveTab]: isOnSaveTab,
            })}
          >
            <div className={styles.topContainer}>
              <Typography
                variant="h6"
                className={styles.title}
                color={isActive ? "text.primary" : "text.secondary"}
              >
                {job?.role}
              </Typography>
              {Boolean(job?.sub_role) && (
                <Typography
                  variant="subtitle1"
                  className={styles.subtitle}
                  color="text.secondary"
                >
                  {isMobile ? job?.sub_role : <>&nbsp;&nbsp;{job?.sub_role}</>}
                </Typography>
              )}
            </div>
            <div className={styles.detailsContainer}>
              <div className={styles.detailContainer}>
                <LocationIcon className={styles.locationIcon} />
                <Typography
                  variant="body2"
                  className={styles.detailText}
                  color={isActive ? "text.primary" : "text.secondary"}
                >
                  {job?.city}
                </Typography>
              </div>

              <div className={styles.detailContainer}>
                <BuildingIcon className={styles.locationIcon} />
                <Typography
                  variant="body2"
                  className={styles.detailText}
                  color={isActive ? "text.primary" : "text.secondary"}
                >
                  {job?.partner_name}
                </Typography>
              </div>
              <div className={styles.detailContainer}>
                <IndustryIcon className={styles.locationIcon} />
                <Typography
                  variant="body2"
                  className={styles.detailText}
                  color={isActive ? "text.primary" : "text.secondary"}
                >
                  {job?.industry}
                </Typography>
              </div>
              {(Boolean(job?.salary_from) || Boolean(job?.salary_to)) && (
                <div className={styles.detailContainer}>
                  <GlobalDollarIcon className={styles.locationIcon} />
                  <Typography
                    variant="body2"
                    className={styles.detailText}
                    color={isActive ? "text.primary" : "text.secondary"}
                  >
                    {`${job?.salary_from ? "$" : ""}${job?.salary_from || ""}${
                      job?.salary_from && job.salary_to ? " - " : ""
                    }${job?.salary_to ? "$" : ""}${job?.salary_to || ""} ${
                      job?.salary_type == "hourly"
                        ? "per hour"
                        : job?.salary_type || ""
                    }`}
                  </Typography>
                </div>
              )}
            </div>

            <Typography
              variant="body2"
              color="text.secondary"
              className={styles.summery}
            >
              {Boolean(job?.summary) ? job?.summary : ""}
            </Typography>
            <div className={styles.skillsContainer}>
              {!showFallbackImage && (
                <>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    alt="logo"
                    src={logoImageUrl}
                    className={styles.mobileLogoContainer}
                    onError={addFallbackImage}
                  />
                </>
              )}
              <IconButton
                className={cx(styles.mobileSaveButton, {})}
                onClick={handleSave}
              >
                <SaveIcon
                  variant={isSaved ? "contained" : "outlined"}
                  className={cx(styles.saveIcon, {
                    [styles.unSaveIcon]: !isSaved,
                  })}
                />
              </IconButton>
            </div>
            {/* <Typography variant="caption" className={styles.link}>
              As shown on{" "}
              <Typography variant="caption" component="a" onClick={goToJob}>
                {getDomain()}
              </Typography>
            </Typography> */}
          </div>
          <div className={styles.rightColumn}>
            <div className={styles.actionContainer}>
              <IconButton className={styles.saveButton} onClick={handleSave}>
                <SaveIcon
                  variant={isSaved ? "contained" : "outlined"}
                  className={cx(styles.saveIcon, {
                    [styles.unSaveIcon]: !isSaved,
                  })}
                />
              </IconButton>
            </div>
            {!showFallbackImage && (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  alt="logo"
                  src={logoImageUrl}
                  className={styles.logoContainer}
                  onError={addFallbackImage}
                />
              </>
            )}
          </div>
        </div>
      </ClickableCard>
    </>
  );
};

export default JobCard;
